import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot, CanActivateFn, RedirectCommand, Router, RouterStateSnapshot,
} from '@angular/router';
import { AGENT_JOIN_TOKEN } from './credee-agent-join.contract';

/**
 * Lendee call-start flags (session ed63d847, 2026-09-10, call 5716).
 *
 * The live call's turn-control plane (`barge-flags.ts` on the test_master lineage —
 * NOT present in this branch or in master) reads a per-call override from the call
 * URL: `?bargeFlags={"<flag>":false}` (see `readBargeFlags` in the call component
 * there). This guard plants that override for LENDEE calls only, so the fix rides
 * this branch into test_master/master without touching the control plane itself.
 * Where the control plane is absent (this branch, master today) the param is inert.
 *
 * What it turns off and why — `interruptContextCue`: after every VAD barge-in the
 * control plane sends the model a "(system: the prospect interrupted you…)" cue as a
 * FORCED turn (a cue is always answered — the session's own contract). The cue gate
 * releases it when the turn state "settles" 900 ms after speech ONSET (the settle
 * clock runs on transcript deltas, and the first delta lands 1-5 s after onset), so
 * it fired mid-sentence before a single prospect word had arrived (cue.gated sent,
 * deferredMs 935-1853), and the model answered it with nothing to answer: "Sorry, go
 * ahead - you were saying?" spoken OVER the prospect, four times in ninety seconds.
 * Nothing prompt-side produces that phrase. With the cue off, Gemini answers the
 * prospect's words on its own end-of-speech (this branch's behaviour today); the
 * reply watchdog still carries the heard-tail context when a reply is late, and the
 * repeat detector still flushes a resumed tail.
 *
 * Remove this guard once the control plane itself stops forcing that turn.
 */
export const LENDEE_CALL_BARGE_FLAGS: Readonly<Record<string, boolean>> =
  Object.freeze({ interruptContextCue: false });

/** Query param the control plane reads (`readBargeFlags`). */
export const BARGE_FLAGS_PARAM = 'bargeFlags';

/** Mirrors CredeeDemoCallComponent.PRODUCT_TOKEN_KEY (token-less re-entries, reloads). */
const PRODUCT_TOKEN_KEY = 'credeeDemoProductToken';

/**
 * The query params the call route must be re-entered with, or null when nothing
 * needs adding: not a Lendee call, or an explicit `?bargeFlags=` is already there
 * (a field override always wins, and this is also what stops a redirect loop).
 * Pure — the guard below supplies the storage read.
 */
export function lendeeCallFlagsRedirect(
  query: Readonly<Record<string, unknown>>,
  storedToken: string | null,
): Record<string, unknown> | null {
  const raw = typeof query['token'] === 'string' ? (query['token'] as string).trim() : '';
  const token = (raw && raw !== AGENT_JOIN_TOKEN ? raw : '') || (storedToken || '').trim();
  if (token.toLowerCase() !== 'lendee') return null;
  if (typeof query[BARGE_FLAGS_PARAM] === 'string') return null;
  return { ...query, [BARGE_FLAGS_PARAM]: JSON.stringify(LENDEE_CALL_BARGE_FLAGS) };
}

export const lendeeCallFlagsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  let stored: string | null = null;
  try { stored = sessionStorage.getItem(PRODUCT_TOKEN_KEY); } catch { /* private mode */ }
  const queryParams = lendeeCallFlagsRedirect(route.queryParams, stored);
  if (!queryParams) return true;
  const router = inject(Router);
  const path = state.url.split('?')[0].split('#')[0];
  // replaceUrl: the param-less entry must not stay in history, or Back re-enters it
  // and this guard redirects again.
  return new RedirectCommand(
    router.createUrlTree([path], { queryParams, fragment: route.fragment ?? undefined }),
    { replaceUrl: true },
  );
};
